import { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';

import type { TagProps } from 'app-components/flux/tags/tagsTypes';

import PayeeAutocomplete from 'components/PayeeAutocomplete';
import SDatePicker from 'components/SDatePicker';
import {
  makeNumericKeyDownHandler,
  sanitizeNumericValue,
} from 'components/Forms/InvestmentTransactionEditForm/legacy/numericInputHelpers';

import type { PayeeAutocompleteProps, SDatePickerProps, TagValue } from './types';

export { sanitizeNumericValue };

export const TypedSDatePicker = SDatePicker as React.ComponentType<SDatePickerProps>;
export const TypedPayeeAutocomplete = PayeeAutocomplete as React.ComponentType<PayeeAutocompleteProps>;

export const handleNumericKeyDown = makeNumericKeyDownHandler();
export const handleIntegerKeyDown = makeNumericKeyDownHandler({ allowDecimal: false });

export const tagToProps = (tag: TagValue): TagProps => ('toObject' in tag ? tag.toObject() : tag);
export const tagToId = (tag: TagValue): string => tagToProps(tag).id ?? '';

export const numberFromInput = (value: string): number => Number(value);

type NumericTextFieldOptions = {
  value: number | null | undefined;
  onValueChange: (value: number | null) => void;
  sign?: 1 | -1;
  allowDecimal?: boolean;
};

const numberFromNumericInput = (value: string): number | null => {
  if (value === '') return null;
  if (value === '.') return 0;

  const result = Number(value);
  return Number.isNaN(result) ? null : result;
};

const numericInputValue = (value: number | null | undefined): string =>
  value == null || Number.isNaN(value) ? '' : String(Math.abs(value));

export function useNumericTextField({ value, onValueChange, sign = 1, allowDecimal = true }: NumericTextFieldOptions): {
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
} {
  const [inputValue, setInputValue] = useState(() => numericInputValue(value));

  useEffect(() => {
    // Keep external form resets in sync while preserving intermediate text like "." or "0." mid-typing.
    const parsedInputValue = numberFromNumericInput(inputValue);
    const absoluteValue = value == null || Number.isNaN(value) ? null : Math.abs(value);
    const inputMatchesValue = absoluteValue == null ? inputValue === '' : parsedInputValue === absoluteValue;

    if (!inputMatchesValue) {
      setInputValue(numericInputValue(value));
    }
  }, [inputValue, value]);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const sanitized = sanitizeNumericValue(event.target.value, { allowDecimal });
      const parsedValue = numberFromNumericInput(sanitized);

      // Store the raw sanitized text so fractional quantities below 0.1 can be typed progressively.
      setInputValue(sanitized);
      onValueChange(parsedValue == null ? null : sign * parsedValue);
    },
    [allowDecimal, onValueChange, sign],
  );

  return { value: inputValue, onChange };
}
